import { SocketService } from '@evoke/game-client';
import { logger } from './logger';
import type { Player, ScoringDetails } from './models';

// Re-export types from models for backward compatibility
export type {
  DixitCard,
  Player,
  Room,
  EndCondition,
  ScoringDetails,
  HistoryEvent,
  CardOnTable,
  PlayerScoring
} from './models';

export function getEntrySource(): 'landing' | 'invite' | 'create' | 'resume' {
  if (sessionStorage.getItem('evoke-entry-room') !== window.location.pathname.split('/')[2]) return 'invite';
  const source = sessionStorage.getItem('evoke-entry-source');
  return source === 'landing' || source === 'create' || source === 'resume' ? source : 'invite';
}

// Generate a simple UUID for persistent player identification
function generateUUID(): string {
  return 'pid_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 11);
}

// Get or create persistent player ID
export function getPersistentPlayerId(): string {
  const existing = localStorage.getItem('evoke-persistent-id');
  if (existing) return existing;

  const newId = generateUUID();
  localStorage.setItem('evoke-persistent-id', newId);
  return newId;
}

export function getReconnectToken(): string {
  const stored = localStorage.getItem('evoke-reconnect-token');
  if (stored && /^[a-f0-9]{64}$/.test(stored)) return stored;
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const token = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  localStorage.setItem('evoke-reconnect-token', token);
  return token;
}

// Utility function to format compact score summary in table format per player
export function formatCompactScoreSummary(
  scoringDetails: ScoringDetails, 
  votes?: Record<string, number>, 
  submissions?: Record<string, number>,
  players?: Player[]
): string {
  if (!votes || !submissions || !players) {
    // Fallback to simple format if we don't have voting data
    const scoreSummary = scoringDetails.playerScoring
      .map(player => `${player.playerName}: ${player.pointsEarned > 0 ? '+' : ''}${player.pointsEarned} pts`)
      .join('\n');
    return scoreSummary;
  }

  // Check for perfect score scenarios (everyone gets +2 except storyteller gets 0)
  if (scoringDetails.perfectScore) {
    const storytellerName = scoringDetails.storytellerName;
    if (scoringDetails.storytellerVotes === 0) {
      return `The story proved too difficult! Everyone gets +2 except ${storytellerName}`;
    } else if (scoringDetails.storytellerVotes === players.length - 1) {
      return `The story was too easy to guess! Everyone gets +2 except ${storytellerName}`;
    }
  }

  // Create maps for easier lookup
  const cardToPlayer = new Map<number, string>();
  Object.entries(submissions).forEach(([playerId, cardId]) => {
    const player = players.find(p => p.id === playerId);
    if (player) {
      cardToPlayer.set(cardId, player.nickname);
    }
  });
  
  // Add storyteller's card to the map
  cardToPlayer.set(scoringDetails.storytellerCardId, scoringDetails.storytellerName);

  // Count votes received by each player's card
  const votesReceived = new Map<string, string[]>();
  Object.entries(votes).forEach(([voterId, cardId]) => {
    const voter = players.find(p => p.id === voterId);
    const cardOwner = cardToPlayer.get(cardId);
    
    if (voter && cardOwner && voter.nickname !== cardOwner) {
      if (!votesReceived.has(cardOwner)) {
        votesReceived.set(cardOwner, []);
      }
      votesReceived.get(cardOwner)!.push(voter.nickname);
    }
  });

  // Find who each player voted for
  const playerVotes = new Map<string, string>();
  Object.entries(votes).forEach(([voterId, cardId]) => {
    const voter = players.find(p => p.id === voterId);
    const cardOwner = cardToPlayer.get(cardId);
    
    if (voter && cardOwner && voter.nickname !== cardOwner) {
      playerVotes.set(voter.nickname, cardOwner);
    }
  });

  // Generate table-like format for each player
  const playerLines = scoringDetails.playerScoring.map(playerScore => {
    const points = `${playerScore.pointsEarned > 0 ? '+' : ''}${playerScore.pointsEarned} pts`;
    let explanation = '';

    if (playerScore.playerName === scoringDetails.storytellerName) {
      // Storyteller explanation
      const guessers = votesReceived.get(playerScore.playerName) || [];
      if (guessers.length === 0) {
        explanation = 'storyteller, nobody guessed their card';
      } else if (guessers.length === players.length - 1) {
        explanation = 'storyteller, everyone guessed their card';
      } else {
        explanation = `storyteller, ${guessers.length} player${guessers.length > 1 ? 's' : ''} guessed their card`;
      }
    } else {
      // Non-storyteller explanation
      const explanations = [];
      
      // Check if they voted for the storyteller
      const votedFor = playerVotes.get(playerScore.playerName);
      if (votedFor === scoringDetails.storytellerName) {
        explanations.push('guessed storyteller');
      } else if (votedFor) {
        explanations.push(`voted for ${votedFor}`);
      }
      
      // Check votes received
      const receivedVotes = votesReceived.get(playerScore.playerName) || [];
      if (receivedVotes.length > 0) {
        explanations.push(`${receivedVotes.length} vote${receivedVotes.length > 1 ? 's' : ''} received`);
      }
      
      if (explanations.length === 0) {
        explanation = 'no points earned';
      } else {
        explanation = explanations.join(', ');
      }
    }

    return `${playerScore.playerName}: ${points} (${explanation})`;
  });

  return playerLines.join('\n');
}

export const socketService = new SocketService({
  serverUrl: () => import.meta.env.VITE_SOCKET_URL || window.location.origin,
  credentials: () => ({ persistentId: getPersistentPlayerId(), reconnectToken: getReconnectToken() }),
  analytics: () => ({ platform: 'web', source: getEntrySource() }),
  log: (message, context) => logger.info(message, context),
});